import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { isAdmin } from "@/lib/adminAuth";
import { Loader2 } from "lucide-react";

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
}

export function ProtectedAdminRoute({ children }: ProtectedAdminRouteProps) {
  const { user, loading } = useAuth();
  const [isAdminUser, setIsAdminUser] = useState<boolean | null>(null);

  useEffect(() => {
    async function checkAdmin() {
      if (loading) return; // wait for auth to initialize
      if (!user) {
        setIsAdminUser(false);
        return;
      }
      try {
        console.log('[AdminGuard] Checking admin for user:', user.id, user.email);
        const adminStatus = await isAdmin(user.id);
        console.log('[AdminGuard] isAdmin result:', adminStatus);
        setIsAdminUser(adminStatus);
      } catch (e) {
        console.error('[AdminGuard] error while checking admin', e);
        setIsAdminUser(false);
      }
    }
    checkAdmin();
  }, [user, loading]);

  // Loading state
  if (loading || isAdminUser === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Not admin
  if (!isAdminUser) {
    console.log('[AdminGuard] Access denied - redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }

  // Admin user - render children
  return <>{children}</>;
}
